import Card from "@/components/Card"
import Combobox from "@/components/Combobox"
import FormHeader from "@/components/FormHeader"
import InputError from "@/components/InputError"
import SkeletonCard from "@/components/SkeletonCard"
import { BackgroundGradient } from "@/components/ui/background-gradient"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { listLokasiKuliah } from "@/constants"
import useDataPendaftaran from "@/hooks/useDataPendaftaran"
import useSessionStorage from "@/hooks/useSessionStorage"
import useTimePeriode from "@/hooks/useTimePeriode"
import { useUser } from "@/hooks/useUser"
import api from "@/lib/axios"
import {
  getListDataPilihanProdi,
  getListProdiPerPeriode,
} from "@/services/getData"
import {
  DataPilihanProdi,
  KonsentrasiProdi,
  LokasiKuliah,
  SysProgramStudi,
} from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { GoMultiSelect } from "react-icons/go"
import { IoWarning } from "react-icons/io5"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import ListProdi from "./partial/ListProdi"

type ProdiSelection = {
  uk_id: string
  jenjang: string
  pilihan_lokasi_kuliah: LokasiKuliah | null
  pilihan_prodi_ke: number
  is_vokasi: boolean
  kelas_kuliah: LokasiKuliah[]
  konsentrasi_prodi?: string
}

type PilihanProdiState = {
  id_data_pendaftaran: string
  jumlah_pilihan_prodi: string
  list_prodi: ProdiSelection[]
}

type OptionProdi = {
  label: string
  value: string
  unitkerjaGroupKey: string
  jenjang: string
  isActive: boolean
  kelasKuliah: LokasiKuliah[]
  isVokasi: boolean
  konsentrasiProdi: KonsentrasiProdi[]
}

const saveProdi = async (form: PilihanProdiState) => {
  const response = await api.patch(
    "/siswa/penerimaan/smbjm/pilihan-prodi/update",
    form,
    { withCredentials: true }
  )
  return response.data
}

export default function Prodi({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string
}) {
  const { user } = useUser()
  // const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  })

  const [form, setForm] = useState<PilihanProdiState>({
    id_data_pendaftaran: idDataPendaftaran,
    jumlah_pilihan_prodi: "",
    list_prodi: [],
  })
  const [openListProdi, setOpenListProdi] = useState(
    Array(Number(form.jumlah_pilihan_prodi)).fill(false)
  )
  const [openKonsentrasiProdi, setOpenKonsentrasiProdi] = useState(false)
  const [selectedProdi, setSelectedProdi] = useState<OptionProdi | null>(null)

  const { timePassedActive } = useTimePeriode()
  const { isValidPendaftaran } = useDataPendaftaran()

  const { data: dataProdi, isPending: isPendingDataProdi } = useQuery({
    queryKey: ["listProdi"],
    queryFn: () => getListProdiPerPeriode(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  })

  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () =>
      getListDataPilihanProdi(sessionPeriode?.id_sys_periode || ""),
  })

  // const ukIds =
  //   form.list_prodi.length > 0
  //     ? form.list_prodi.map((prodi) => prodi?.uk_id)
  //     : [];

  // const { data: dataUkt, isPending: isPendingDataUkt } = useQuery({
  //   queryKey: ["listUkt", form.list_prodi],
  //   queryFn: () => getListUktPerProdi(ukIds),
  //   enabled: !!form.list_prodi.length,
  // });

  const optionsProdi = dataProdi?.list_prodi?.map(
    (sysProdi: SysProgramStudi) => {
      return {
        label: sysProdi?.unitkerja?.nama,
        value: `${sysProdi?.uk_id}`,
        unitkerjaGroupKey: sysProdi?.unitkerja?.unitkerja_group_key,
        jenjang: sysProdi?.unitkerja?.uk_program,
        isActive: sysProdi?.unitkerja?.is_active,
        kelasKuliah: sysProdi?.unitkerja?.kelas_kuliah,
        isVokasi: sysProdi?.unitkerja?.is_vokasi,
        konsentrasiProdi: sysProdi.unitkerja?.konsentrasi_prodi,
      }
    }
  )

  const filteredDataProdi = optionsProdi?.filter((option: OptionProdi) => {
    return option?.unitkerjaGroupKey == "5" && option.isActive
  })

  const optionsKonsentrasiProdi = selectedProdi
    ? selectedProdi?.konsentrasiProdi?.map((item: KonsentrasiProdi) => {
        return {
          label: item.nama_konsentrasi,
          value: `${item.id_ref_konsentrasi_prodi}`,
        }
      })
    : []

  const queryClient = useQueryClient()
  const {
    mutate,
    isPending: isPendingSaveProdi,
    error,
  } = useMutation({
    mutationFn: saveProdi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }
  useEffect(() => {
    if (!dataPilihanProdi) return

    setForm((prev) => ({
      ...prev,
      jumlah_pilihan_prodi: `${
        dataPilihanProdi?.length === 0 ? 1 : dataPilihanProdi.length
      }`,
      list_prodi: dataPilihanProdi?.map((item: DataPilihanProdi) => {
        return {
          uk_id: `${item?.uk_id}`,
          jenjang: item.unitkerja?.uk_program,
          pilihan_lokasi_kuliah: item.pilihan_lokasi_kuliah,
          pilihan_prodi_ke: item.urutan_program_studi,
          is_vokasi: item.unitkerja?.is_vokasi,
          kelas_kuliah: item.unitkerja?.kelas_kuliah,
          konsentrasi_prodi: item.id_ref_konsentrasi_prodi,
        }
      }),
    }))

    dataPilihanProdi?.map((item: DataPilihanProdi) => {
      const selected: OptionProdi = {
        label: item.unitkerja?.nama || "",
        value: String(item.uk_id) || "",
        isActive: item.unitkerja?.is_active ?? false,
        isVokasi: item.unitkerja?.is_vokasi ?? false,
        jenjang: item.unitkerja?.uk_program || "",
        kelasKuliah: item.unitkerja?.kelas_kuliah || [],
        konsentrasiProdi: item.unitkerja?.konsentrasi_prodi || [],
        unitkerjaGroupKey: item.unitkerja?.unitkerja_group_key || "",
      }

      if (selected.value) setSelectedProdi(selected)
    })
  }, [dataPilihanProdi])

  return (
    <div className="flex lg:flex-row flex-col lg:gap-5 gap-3">
      <Card className="h-fit lg:w-[50%]">
        <div className="mb-3 rounded-xl border flex overflow-hidden">
          <div
            className={`lg:py-2 py-1 lg:px-4 px-2 flex-1 font-semibold lg:text-base text-sm`}
          >
            Daftar Program Studi
          </div>
        </div>
        <ListProdi dataProdi={dataProdi} isPending={isPendingDataProdi} />
      </Card>
      <div className="flex-1 overflow-auto h-fit rounded-xl">
        {isPendingDataProdi ? (
          <SkeletonCard />
        ) : (
          <BackgroundGradient>
            <Card className="bg-white flex-1 overflow-auto">
              <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
                <FormHeader
                  title="Memilih Program Studi"
                  icon={<GoMultiSelect />}
                />
                {/* <div>
                <RadioGroup
                  required
                  defaultValue="comfortable"
                  onValueChange={(value: string) =>
                    setForm((prev) => ({
                      ...prev,
                      jumlah_pilihan_prodi: value,
                      list_prodi: [],
                    }))
                  }
                >
                  <Label className="mb-1">Pilih Jumlah Pilihan Prodi</Label>
                  <div className="flex gap-10">
                    {["1", "2", "3", "4"].map(
                      (value: string, index: number) => {
                        return (
                          <div
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem
                              required={form.jumlah_pilihan_prodi == ""}
                              id={`${index}`}
                              value={value}
                              checked={form.jumlah_pilihan_prodi === value}
                            />
                            <Label htmlFor={`${index}`}>{value}</Label>
                          </div>
                        );
                      }
                    )}
                  </div>
                </RadioGroup>
              </div> */}

                <div className="flex flex-col gap-y-3">
                  {form.jumlah_pilihan_prodi &&
                    Array(Number(form.jumlah_pilihan_prodi))
                      .fill(null)
                      .map((_, index) => {
                        // const isKelasDenpasar =
                        //   form.list_prodi[index]?.is_kelas_denpasar;

                        return (
                          <div key={index} className={`flex flex-col gap-3`}>
                            <div className="flex-1">
                              <Label>Pilihan Program Studi</Label>
                              <Combobox
                                open={openListProdi[index]}
                                setOpen={(isOpen) =>
                                  setOpenListProdi((prevOpenListProdi) => {
                                    const newOpenList = [...prevOpenListProdi]
                                    newOpenList[index] = isOpen
                                    return newOpenList
                                  })
                                }
                                value={form.list_prodi[index]?.uk_id || ""}
                                setValue={(value) => {
                                  const selected: OptionProdi =
                                    filteredDataProdi?.find(
                                      (item: OptionProdi) =>
                                        item.value === value
                                    )
                                  setSelectedProdi(selected)

                                  const isDuplicate =
                                    form.list_prodi.length > 0
                                      ? form.list_prodi.some(
                                          (prodi) => prodi?.uk_id === value
                                        )
                                      : false

                                  if (isDuplicate) {
                                    toast.error("Prodi ini sudah anda pilih.")
                                    return
                                  }

                                  setForm((prev) => {
                                    const newListProdi = [...prev.list_prodi]
                                    newListProdi[index] = {
                                      uk_id: value,
                                      jenjang: selected?.jenjang,
                                      pilihan_lokasi_kuliah: null,
                                      pilihan_prodi_ke: index + 1,
                                      is_vokasi: selected?.isVokasi,
                                      kelas_kuliah: selected?.kelasKuliah,
                                    }
                                    return {
                                      ...prev,
                                      list_prodi: newListProdi,
                                    }
                                  })
                                }}
                                options={filteredDataProdi}
                                placeholder="Pilih prodi"
                              />
                            </div>

                            {selectedProdi &&
                            selectedProdi?.konsentrasiProdi?.length > 0 ? (
                              <div>
                                <Label>Konsentrasi Prodi</Label>
                                <Combobox
                                  open={openKonsentrasiProdi}
                                  setOpen={setOpenKonsentrasiProdi}
                                  value={
                                    form.list_prodi[index]?.konsentrasi_prodi ||
                                    ""
                                  }
                                  setValue={(value) => {
                                    setForm((prev) => {
                                      const newListProdi = [...prev.list_prodi]
                                      newListProdi[index] = {
                                        ...newListProdi[index],
                                        konsentrasi_prodi: value,
                                      }
                                      return {
                                        ...prev,
                                        list_prodi: newListProdi,
                                      }
                                    })
                                  }}
                                  options={optionsKonsentrasiProdi}
                                  placeholder="Pilih konsentrasi prodi"
                                />
                                {error?.response?.data?.errors?.[
                                  `list_prodi.${index}.konsentrasi_prodi`
                                ] &&
                                  error?.response?.data?.errors?.[
                                    `list_prodi.${index}.konsentrasi_prodi`
                                  ].length > 0 && (
                                    <InputError
                                      text={
                                        "Pilihan konsentrasi prodi harus diisi"
                                      }
                                    />
                                  )}
                              </div>
                            ) : (
                              ""
                            )}

                            {form.list_prodi[index]?.kelas_kuliah &&
                            form.list_prodi[index]?.kelas_kuliah.length > 1 ? (
                              <div className="flex-1">
                                <Label>Lokasi Kuliah</Label>
                                <Select
                                  value={
                                    form.list_prodi[index]
                                      ?.pilihan_lokasi_kuliah || ""
                                  }
                                  onValueChange={(value) => {
                                    setForm((prev) => {
                                      const newListProdi = [...prev.list_prodi]
                                      newListProdi[index] = {
                                        ...newListProdi[index],
                                        pilihan_lokasi_kuliah:
                                          value.toLowerCase() as LokasiKuliah,
                                      }
                                      return {
                                        ...prev,
                                        list_prodi: newListProdi,
                                      }
                                    })
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Pilih Lokasi Kuliah">
                                      {form.list_prodi[index]
                                        ?.pilihan_lokasi_kuliah &&
                                        listLokasiKuliah.find(
                                          (loc) =>
                                            loc.toLowerCase() ===
                                            form.list_prodi[index]
                                              ?.pilihan_lokasi_kuliah
                                        )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {listLokasiKuliah.map((value, idx) => (
                                      <SelectItem
                                        key={idx}
                                        value={value.toLowerCase()}
                                      >
                                        {value}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {error?.response?.data?.errors?.[
                                  `list_prodi.${index}.pilihan_lokasi_kuliah`
                                ] &&
                                  error?.response?.data?.errors?.[
                                    `list_prodi.${index}.pilihan_lokasi_kuliah`
                                  ].length > 0 && (
                                    <InputError
                                      text={
                                        "Pilihan lokasi kuliah harus diisi."
                                      }
                                    />
                                  )}
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        )
                      })}

                  {error && error?.response?.data?.message && (
                    <div className="p-3 rounded-xl border border-red-500 bg-red-50 flex gap-3">
                      <IoWarning className="text-red-500" size={30} />
                      <p className="text-red-500">
                        {error?.response?.data?.message}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  {!isValidPendaftaran &&
                  !timePassedActive?.waktu_selesai_daftar_valid_selesai ? (
                    <Button variant={"outline"} disabled={isPendingSaveProdi}>
                      {isPendingSaveProdi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Simpan"
                      )}
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              </form>
            </Card>
          </BackgroundGradient>
        )}

        {/* <div className="mt-3">
          {isPendingDataUkt ? (
            <Card className="flex flex-col gap-y-3">
              {Array(10)
                .fill(null)
                .map((_, index) => {
                  return <SkeletonRow key={index} />;
                })}
            </Card>
          ) : (
            dataUkt && <ListUkt dataUkt={dataUkt} />
          )}
        </div> */}
      </div>
    </div>
  )
}
